import { useRef, useState } from 'react';
import {
  parseShadow,
  serializeShadow,
  DEFAULT_LAYER,
  type ShadowLayer,
} from './shadowValue';

export interface ShadowEditorProps {
  label: string;
  /** Effective value (override if set, else the knob's resolved default). */
  resolved: string;
  /** Whether an explicit override is set — drives the Reset affordance. */
  hasOverride: boolean;
  /** Emit a new CSS value, or `undefined` to clear the override (reset). */
  onChange: (next: string | undefined) => void;
}

const HEX7_RE = /^#([0-9a-fA-F]{6})$/;

/**
 * ShadowEditor — friendly replacement for the raw `box-shadow` text field.
 *
 * Per-layer controls (Drop/Inner, X/Y/Blur/Spread, colour with alpha) plus
 * add/remove and an "Advanced: raw CSS" escape hatch for values the parser
 * can't represent. The on-disk format stays a CSS string (serializeShadow),
 * so stored themes are untouched — this is a pure editor-UX change.
 *
 * Edits only emit on actual user interaction; mounting never writes, so
 * opening the editor and changing nothing leaves the value as-is.
 */
export function ShadowEditor({ label, resolved, hasOverride, onChange }: ShadowEditorProps) {
  const initial = parseShadow(resolved);
  const [layers, setLayers] = useState<ShadowLayer[]>(initial ?? []);
  const [rawMode, setRawMode] = useState(initial === null);
  const [rawText, setRawText] = useState(resolved);

  // Re-sync from the outside (reset / external paste) without clobbering an
  // in-progress edit: `lastValueRef` holds the value WE last emitted, so our
  // own round-trips are ignored and only foreign changes re-seed the fields.
  const lastValueRef = useRef(resolved);
  if (resolved !== lastValueRef.current) {
    lastValueRef.current = resolved;
    const p = parseShadow(resolved);
    setRawText(resolved);
    if (p === null) setRawMode(true);
    else setLayers(p);
  }

  const emit = (next: ShadowLayer[]) => {
    setLayers(next);
    const str = serializeShadow(next);
    lastValueRef.current = str;
    onChange(str);
  };

  const patchLayer = (i: number, patch: Partial<ShadowLayer>) =>
    emit(layers.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLayer = () => emit([...layers, { ...DEFAULT_LAYER }]);
  const removeLayer = (i: number) => emit(layers.filter((_, idx) => idx !== i));

  const applyRaw = (text: string) => {
    setRawText(text);
    lastValueRef.current = text;
    onChange(text === '' ? undefined : text);
  };

  const toRaw = () => {
    setRawText(serializeShadow(layers));
    setRawMode(true);
  };
  const toStructured = () => {
    const p = parseShadow(rawText);
    if (p) {
      setLayers(p);
      setRawMode(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wide text-neutral-400">
          {label}
        </span>
        {hasOverride && (
          <button
            type="button"
            aria-label={`Reset ${label}`}
            onClick={() => onChange(undefined)}
            className="rounded border border-neutral-700 px-2 py-0.5 text-[10px] text-neutral-400 hover:text-white"
          >
            reset
          </button>
        )}
      </div>

      {rawMode ? (
        <div className="flex flex-col gap-1">
          <input
            aria-label={`${label} raw CSS`}
            type="text"
            value={rawText}
            onChange={(e) => applyRaw(e.target.value)}
            placeholder="e.g. 0 2px 8px rgba(0,0,0,0.2)"
            className="rounded border border-neutral-700 bg-neutral-900 px-2 py-1 font-mono text-[12px] text-white outline-none"
          />
          <button
            type="button"
            onClick={toStructured}
            className="self-start text-[10px] text-neutral-400 underline hover:text-white"
          >
            ← back to visual editor
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {layers.length === 0 && (
            <div className="rounded border border-dashed border-neutral-800 px-2 py-3 text-center font-mono text-[10px] text-neutral-500">
              No shadow
            </div>
          )}
          {layers.map((layer, i) => (
            <LayerRow
              key={i}
              layer={layer}
              index={i}
              onPatch={(patch) => patchLayer(i, patch)}
              onRemove={() => removeLayer(i)}
            />
          ))}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={addLayer}
              className="rounded border border-neutral-700 px-2 py-1 font-mono text-[11px] text-neutral-300 hover:text-white"
            >
              + Add shadow layer
            </button>
            <button
              type="button"
              onClick={toRaw}
              className="text-[10px] text-neutral-500 underline hover:text-white"
            >
              Advanced: raw CSS
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function typeBtnClass(active: boolean): string {
  return (
    'rounded border px-2 py-0.5 font-mono text-[10px] transition-colors ' +
    (active
      ? 'border-white bg-white text-black'
      : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:text-white')
  );
}

interface LayerRowProps {
  layer: ShadowLayer;
  index: number;
  onPatch: (patch: Partial<ShadowLayer>) => void;
  onRemove: () => void;
}

function LayerRow({ layer, index, onPatch, onRemove }: LayerRowProps) {
  return (
    <div className="flex flex-col gap-2 rounded border border-neutral-800 p-2">
      <div className="flex items-center justify-between">
        <div
          role="radiogroup"
          aria-label={`Shadow ${index + 1} type`}
          className="flex gap-1"
        >
          <button
            type="button"
            role="radio"
            aria-checked={!layer.inset}
            onClick={() => onPatch({ inset: false })}
            className={typeBtnClass(!layer.inset)}
          >
            Drop
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={layer.inset}
            onClick={() => onPatch({ inset: true })}
            className={typeBtnClass(layer.inset)}
          >
            Inner
          </button>
        </div>
        <button
          type="button"
          aria-label={`Remove shadow ${index + 1}`}
          onClick={onRemove}
          className="px-1 text-[12px] text-neutral-500 hover:text-white"
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-4 gap-1.5">
        <NumField label="X" value={layer.x} onChange={(x) => onPatch({ x })} />
        <NumField label="Y" value={layer.y} onChange={(y) => onPatch({ y })} />
        <NumField label="Blur" value={layer.blur} onChange={(blur) => onPatch({ blur })} />
        <NumField label="Spread" value={layer.spread} onChange={(spread) => onPatch({ spread })} />
      </div>

      <LayerColor value={layer.color} onChange={(color) => onPatch({ color })} />
    </div>
  );
}

interface NumFieldProps {
  label: string;
  value: number;
  onChange: (n: number) => void;
}

function NumField({ label, value, onChange }: NumFieldProps) {
  // Local text state so partial input ("-", "") doesn't snap back mid-typing.
  const [text, setText] = useState(String(value));
  const syncedRef = useRef(value);
  if (value !== syncedRef.current) {
    syncedRef.current = value;
    setText(String(value));
  }
  return (
    <label className="flex flex-col gap-0.5">
      <span className="font-mono text-[9px] uppercase tracking-wide text-neutral-500">
        {label}
      </span>
      <input
        aria-label={label}
        type="number"
        value={text}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          if (t === '' || t === '-') return;
          const n = Number(t);
          if (!Number.isNaN(n)) {
            syncedRef.current = n;
            onChange(n);
          }
        }}
        className="w-full rounded border border-neutral-700 bg-neutral-900 px-1.5 py-1 font-mono text-[11px] text-white outline-none"
      />
    </label>
  );
}

interface LayerColorProps {
  value: string;
  onChange: (c: string) => void;
}

function LayerColor({ value, onChange }: LayerColorProps) {
  // Text field is authoritative and accepts ANY CSS colour (incl. rgba/hex8
  // with alpha). The native swatch only mirrors 6-digit hex.
  const [text, setText] = useState(value);
  const syncedRef = useRef(value);
  if (value !== syncedRef.current) {
    syncedRef.current = value;
    setText(value);
  }
  const swatch = HEX7_RE.test(text) ? text : '#000000';
  const commit = (next: string) => {
    setText(next);
    syncedRef.current = next;
    onChange(next);
  };
  return (
    <div className="flex items-center gap-2">
      <span className="w-10 font-mono text-[9px] uppercase tracking-wide text-neutral-500">
        Color
      </span>
      <input
        aria-label="Shadow colour swatch"
        type="color"
        value={swatch}
        onChange={(e) => commit(e.target.value)}
        className="h-6 w-6 cursor-pointer rounded border border-neutral-700 bg-transparent p-0"
      />
      <input
        aria-label="Shadow colour"
        type="text"
        value={text}
        onChange={(e) => commit(e.target.value)}
        placeholder="rgba(0,0,0,0.2)"
        className="flex-1 rounded border border-neutral-700 bg-neutral-900 px-2 py-1 font-mono text-[11px] text-white outline-none"
      />
    </div>
  );
}
